import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LogoText from "@/components/LogoText";
import { useLocale, useTranslation } from "@/i18n";

type NavItem = { label: string; href?: string; children?: { label: string; href: string }[]; isNew?: boolean };

const DesktopDropdown = ({ item }: { item: NavItem }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { localePath } = useLocale();
  const isActive = item.children?.some((c) => location.pathname === localePath(c.href!));

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-sm font-body font-medium tracking-wide transition-colors duration-300 ${
          isActive ? "text-gold" : "text-foreground/70 hover:text-gold"
        }`}
      >
        {item.label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 min-w-[200px] bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50"
          >
            {item.children?.map((child) => (
              <Link
                key={child.href}
                to={localePath(child.href)}
                onClick={() => setOpen(false)}
                className={`block px-5 py-3 text-sm font-body font-medium transition-colors ${
                  location.pathname === localePath(child.href)
                    ? "text-gold bg-secondary/40"
                    : "text-foreground/70 hover:text-gold hover:bg-secondary/20"
                }`}
              >
                {child.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { locale, localePath, alternatePath } = useLocale();
  const t = useTranslation();

  const navLinks: NavItem[] = [
    { label: t.nav.home, href: "/" },
    { label: t.nav.about, href: "/a-propos" },
    {
      label: t.nav.expertise,
      children: [
        { label: t.nav.services, href: "/services" },
        { label: t.nav.methodology, href: "/methodologie" },
        { label: t.nav.employeMystere, href: "/employe-mystere" },
      ],
    },
    { label: t.nav.cities, href: "/audit-mystere" },
    { label: t.nav.blog, href: "/blog" },
    { label: t.nav.faq, href: "/faq" },
    { label: t.nav.contact, href: "/contact" },
  ];

  return (
    <nav aria-label="Navigation principale" className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto flex items-center justify-between h-20 px-4">
        <LogoText size="sm" />

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <DesktopDropdown key={link.label} item={link} />
            ) : (
              <Link
                key={link.href}
                to={localePath(link.href!)}
                className={`text-sm font-body font-medium tracking-wide transition-colors duration-300 ${
                  location.pathname === localePath(link.href!)
                    ? "text-gold"
                    : "text-foreground/70 hover:text-gold"
                }`}
              >
                {link.label}
              </Link>
            )
          )}

          {/* Language switcher */}
          <Link
            to={alternatePath()}
            className="flex items-center gap-1.5 text-sm font-body font-medium text-foreground/70 hover:text-gold transition-colors duration-300"
            title={locale === "fr" ? "Switch to English" : "Passer en français"}
          >
            <Globe size={16} />
            {locale === "fr" ? "EN" : "FR"}
          </Link>

          <Button variant="gold" size="sm" asChild>
            <Link to={localePath("/contact")}>{t.nav.cta}</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-t border-border overflow-hidden"
          >
            <div className="flex flex-col gap-4 p-6">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <span className="text-xs font-body font-semibold text-gold tracking-wider uppercase mb-2 block">
                      {link.label}
                    </span>
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={localePath(child.href)}
                        onClick={() => setOpen(false)}
                        className={`block pl-4 py-3 min-h-[48px] flex items-center text-base font-body font-medium tracking-wide transition-colors ${
                          location.pathname === localePath(child.href)
                            ? "text-gold"
                            : "text-foreground/70"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={localePath(link.href!)}
                    onClick={() => setOpen(false)}
                    className={`py-3 min-h-[48px] flex items-center text-base font-body font-medium tracking-wide transition-colors ${
                      location.pathname === localePath(link.href!)
                        ? "text-gold"
                        : "text-foreground/70"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}

              {/* Mobile language switcher */}
              <Link
                to={alternatePath()}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 py-3 min-h-[48px] text-base font-body font-medium text-foreground/70"
              >
                <Globe size={18} />
                {locale === "fr" ? "English" : "Français"}
              </Link>

              <Button variant="gold" size="sm" asChild>
                <Link to={localePath("/contact")} onClick={() => setOpen(false)}>
                  {t.nav.cta}
                </Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
